import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMarkerValuations } from '../../../services/markerValuationService';

const MARKETPLACE_BASE = 'https://www.kromarketplace.com';

/* ─────────────────────────── Reference data ─────────────────────────── */

const MARKERS = [
  { denom: 'kro.gipr.genesis.v2',      label: 'Genesis Bond',     symbol: 'KRO-GIPR', units: 60,  price: 1_000_000,  tone: 'warning', impact: 'Foundation capital for trust-based settlement infrastructure. Anchors the KRO Trust Index.' },
  { denom: 'kro.as.accountability.v2', label: 'Accountability',   symbol: 'KRO-AS',   units: 182, price: 5_000_000,  tone: 'primary', impact: 'Enforces transparent governance and verifiable stewardship across every settled draw.' },
  { denom: 'kro.sc.stewardship.v2',    label: 'Stewardship Draw', symbol: 'KRO-SC',   units: 12,  price: 20_000_000, tone: 'success', impact: 'Funds social purpose allocations under the SPC mandate, tied to settlement value.' },
];

const ESG_PILLARS = [
  {
    pillar: 'Governance Integrity', score: 97, icon: 'bi-shield-check', tone: 'success',
    kpis: [
      'Multi-party Daml authorization — no single-party execution',
      'kroOS holds an observer-only role on all settlement contracts',
      'Canton sub-transaction privacy preserves institutional sovereignty',
      'Legal observer parties on encumbrance and close-out events',
    ],
  },
  {
    pillar: 'Collateral Transparency', score: 94, icon: 'bi-eye-fill', tone: 'info',
    kpis: [
      'All 254 on-chain units verifiable on ZoneScan (Provenance block explorer)',
      'DTIF registry IDs per ISO 24165 — globally identifiable tokens',
      'Oracle NAV revaluation on a 15-minute margin monitoring cycle',
      'CIP-0056 allocation locking reserves net amounts, reducing capital drag',
    ],
  },
  {
    pillar: 'Settlement Soundness', score: 96, icon: 'bi-lightning-charge-fill', tone: 'warning',
    kpis: [
      'Deterministic T+0 atomic DvP — both legs settle or both roll back',
      'Canton Network open-source DvP standards',
      'Binary compliance gates that fail closed rather than settle partially',
      '24/7 settlement-ready — no T+1 batch dependency',
    ],
  },
  {
    pillar: 'Social Purpose', score: 88, icon: 'bi-heart-fill', tone: 'danger',
    kpis: [
      'SPC corporate mandate for discretionary social purpose allocation',
      'Stewardship Draw tranche tied to settlement value',
      'ESG audit receipt generated for each social purpose distribution',
    ],
  },
];

const DISTRIBUTION_PARTNERS = [
  { name: '1687 Productions',              network: 'Distribution Channel', esg: 94, outcome: 'Creative production sourced through local crews — 38 roles sustained across three regional markets.', focus: 'Creative & Media' },
  { name: 'AI for Job Security Foundation', network: 'Distribution Channel', esg: 96, outcome: 'Workforce reskilling pathways delivered to 1,240 participants transitioning into technology roles.', focus: 'Workforce Development' },
  { name: 'So Fraiche',                    network: 'Supplier Network',     esg: 91, outcome: 'Short-chain sourcing reduced fulfilment distance by 42% against the prior distribution model.', focus: 'Consumer Goods' },
  { name: 'African Supplier Network',       network: 'Supplier Network',     esg: 93, outcome: 'Cross-border supplier onboarding opened market access for 67 small enterprises.', focus: 'Regional Supply Chain' },
  { name: 'Sexi Cycle',                    network: 'Distribution Channel', esg: 89, outcome: 'Circular product programme diverted 24 tonnes of material from landfill in the current cycle.', focus: 'Circular Economy' },
];

// Existing intelligence surfaces this page rolls up. Kept as links rather than
// duplicated dashboards so there is one home for each dataset.
const INTELLIGENCE_MODULES = [
  { to: '/data/foundational-investment',                       icon: 'bi-bank2',          tone: 'warning', title: 'Foundational Investment',   desc: 'Marker-level valuations, ESG pillar detail, and the linked KRO Marketplace product set.' },
  { to: '/data/analytics-and-intelligence/data-overview',      icon: 'bi-graph-up',       tone: 'primary', title: 'Analytics & Intelligence',  desc: 'Executive ESG data overview, raw intelligence feeds, predictive analytics, and custom reports.' },
  { to: '/ethics/distribution-partners',                       icon: 'bi-diagram-3-fill', tone: 'info',    title: 'Distribution Partners',     desc: 'Partner-by-partner ESG profiles and the distribution channel impact record.' },
  { to: '/ethics/supply-chain-ethics/overview',                icon: 'bi-truck',          tone: 'success', title: 'Supply Chain Ethics',       desc: 'Supplier assessment, ethical sourcing standards, and supply chain compliance scoring.' },
  { to: '/ethics/cultural-and-global-impact/impact-assessment',icon: 'bi-globe2',         tone: 'danger',  title: 'Cultural & Global Impact',  desc: 'Community engagement programmes, localisation, and regional impact assessment.' },
  { to: '/data/investor-dashboard',                            icon: 'bi-briefcase',      tone: 'secondary', title: 'Investor Dashboard',      desc: 'Portfolio-level performance metrics and engagement insights for stewardship reporting.' },
];

/* ─────────────────────────── Helpers ─────────────────────────── */

const shortMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`;
  return `$${v.toLocaleString()}`;
};

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(); } catch { return '—'; } };

const scoreTone = (n) => (n >= 95 ? 'success' : n >= 85 ? 'primary' : n >= 70 ? 'warning' : 'danger');

/* ─────────────────────────── Component ─────────────────────────── */

const ESGIntelligence = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { markers: liveMarkers, isLive, refreshedAt } = useMarkerValuations(60_000);

  useEffect(() => { document.title = 'ESG Intelligence — KRO Exchange'; }, []);

  // Overlay live on-chain supply onto the static marker metadata; falls back to
  // the last verified values when the oracle is unreachable.
  const markers = MARKERS.map(m => {
    const live = liveMarkers[m.denom];
    return live
      ? { ...m, units: live.units, price: live.price_per_unit_usd, total: live.total_value_usd }
      : { ...m, total: m.units * m.price };
  });

  const totalAUM = markers.reduce((s, m) => s + m.total, 0);
  const totalUnits = markers.reduce((s, m) => s + m.units, 0);
  const compositeScore = Math.round(ESG_PILLARS.reduce((s, p) => s + p.score, 0) / ESG_PILLARS.length);
  const avgPartnerEsg = Math.round(DISTRIBUTION_PARTNERS.reduce((s, p) => s + p.esg, 0) / DISTRIBUTION_PARTNERS.length);

  /* ── Presentational helpers ──────────────────────────────────── */

  const KpiCard = ({ icon, label, value, sub, tone }) => (
    <div className="col-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className={`bi ${icon} text-${tone}`}></i>
            <span className="text-muted text-uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.6px', fontWeight: 700 }}>{label}</span>
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem' }}>{value}</div>
          <small className="text-muted">{sub}</small>
        </div>
      </div>
    </div>
  );

  /* ─────────────────────────── Render ─────────────────────────── */

  return (
    <div className="container-fluid py-4">

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/kro-exchange/settlement-hub">KRO Exchange</Link></li>
          <li className="breadcrumb-item active" aria-current="page">ESG Intelligence</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h1 className="display-6 fw-bold mb-1">
            <i className="bi bi-globe2 me-2 text-primary"></i>ESG Intelligence
          </h1>
          <p className="text-muted mb-1" style={{ fontSize: '0.88rem' }}>
            {shortMoney(totalAUM)}+ under stewardship · $37.3B+ settlement capacity · impact across all KRO Exchange networks.
          </p>
          <small className="d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
            <span className="rounded-circle d-inline-block" style={{ width: 7, height: 7, background: isLive ? '#198754' : '#fd7e14' }} />
            <span className="text-muted">
              {isLive
                ? `Live — Provenance oracle${refreshedAt ? ` · updated ${fmtTime(refreshedAt)}` : ''}`
                : 'Showing last verified values — oracle unavailable'}
            </span>
          </small>
        </div>
        <div className="d-flex gap-2">
          <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            <i className="bi bi-shop me-1"></i>Browse Products
          </a>
        </div>
      </div>

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <KpiCard icon="bi-safe2"        label="Under Stewardship" tone="primary" value={shortMoney(totalAUM)} sub={`${totalUnits} on-chain units — ZoneScan verifiable`} />
        <KpiCard icon="bi-award-fill"   label="Composite ESG"     tone={scoreTone(compositeScore)} value={compositeScore} sub="Across four assessed pillars" />
        <KpiCard icon="bi-people-fill"  label="Partner ESG Avg"   tone={scoreTone(avgPartnerEsg)} value={avgPartnerEsg} sub={`${DISTRIBUTION_PARTNERS.length} active distribution and supplier partners`} />
        <KpiCard icon="bi-lightning-charge-fill" label="Settlement Capacity" tone="info" value="$37.3B+" sub="Revolving intraday facility — regenerates each cycle" />
      </div>

      {/* Tabs */}
      <ul className="nav nav-pills mb-4" style={{ gap: '0.25rem' }}>
        {[
          { id: 'overview', icon: 'bi-speedometer2',   label: 'Impact Overview' },
          { id: 'pillars',  icon: 'bi-columns-gap',    label: 'ESG Pillars' },
          { id: 'partners', icon: 'bi-people',         label: 'Network Impact' },
          { id: 'modules',  icon: 'bi-grid-1x2',       label: 'Intelligence Modules' },
        ].map(t => (
          <li key={t.id} className="nav-item">
            <button className={`nav-link ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ══════════════════ TAB: IMPACT OVERVIEW ══════════════════ */}
      {activeTab === 'overview' && (
        <div className="row g-4">
          <div className="col-12 col-xl-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-coin me-2 text-primary"></i>Stewardship Assets</h6>
                <small className="text-muted">On-chain marker denoms and the impact each one carries.</small>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr style={{ fontSize: '0.76rem' }}>
                        <th className="ps-3">Marker</th>
                        <th className="text-end">Units</th>
                        <th className="text-end">Value</th>
                        <th className="pe-3">Impact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {markers.map(m => (
                        <tr key={m.denom} style={{ fontSize: '0.8rem' }}>
                          <td className="ps-3">
                            <div className="fw-semibold">
                              <span className={`badge bg-${m.tone} me-2`} style={{ fontSize: '0.6rem' }}>{m.symbol}</span>
                              {m.label}
                            </div>
                            <code style={{ fontSize: '0.68rem' }}>{m.denom}</code>
                          </td>
                          <td className="text-end fw-semibold">{m.units}</td>
                          <td className="text-end fw-bold">{shortMoney(m.total)}</td>
                          <td className="text-muted pe-3" style={{ fontSize: '0.76rem' }}>{m.impact}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr style={{ fontSize: '0.82rem' }}>
                        <td className="ps-3 fw-bold">Total</td>
                        <td className="text-end fw-bold">{totalUnits}</td>
                        <td className="text-end fw-bold">{shortMoney(totalAUM)}</td>
                        <td className="pe-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Supply is read on-chain from Provenance <code style={{ fontSize: '0.68rem' }}>pio-mainnet-1</code> and multiplied by the institutional unit price.
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-award me-2 text-primary"></i>Pillar Scores</h6>
              </div>
              <div className="card-body">
                {ESG_PILLARS.map(p => (
                  <div key={p.pillar} className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold" style={{ fontSize: '0.83rem' }}>
                        <i className={`bi ${p.icon} me-2 text-${p.tone}`}></i>{p.pillar}
                      </span>
                      <span className={`fw-bold text-${scoreTone(p.score)}`}>{p.score}</span>
                    </div>
                    <div className="progress" style={{ height: 6 }}>
                      <div className={`progress-bar bg-${p.tone}`} style={{ width: `${p.score}%` }} />
                    </div>
                  </div>
                ))}
                <div className="d-flex justify-content-between align-items-center pt-3 mt-3 border-top">
                  <span className="fw-bold">Composite</span>
                  <span className={`badge bg-${scoreTone(compositeScore)}`} style={{ fontSize: '0.8rem' }}>{compositeScore}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: ESG PILLARS ══════════════════ */}
      {activeTab === 'pillars' && (
        <div className="row g-3">
          {ESG_PILLARS.map(p => (
            <div key={p.pillar} className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold" style={{ fontSize: '0.92rem' }}>
                    <i className={`bi ${p.icon} me-2 text-${p.tone}`}></i>{p.pillar}
                  </h6>
                  <span className={`badge bg-${scoreTone(p.score)}`} style={{ fontSize: '0.75rem' }}>{p.score}</span>
                </div>
                <div className="card-body">
                  <div className="progress mb-3" style={{ height: 6 }}>
                    <div className={`progress-bar bg-${p.tone}`} style={{ width: `${p.score}%` }} />
                  </div>
                  <ul className="list-unstyled mb-0">
                    {p.kpis.map(k => (
                      <li key={k} className="d-flex gap-2 mb-2">
                        <i className={`bi bi-check-circle-fill text-${p.tone} flex-shrink-0`} style={{ fontSize: '0.8rem', marginTop: 2 }}></i>
                        <span className="text-muted" style={{ fontSize: '0.79rem' }}>{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════ TAB: NETWORK IMPACT ══════════════════ */}
      {activeTab === 'partners' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <h6 className="mb-0 fw-bold"><i className="bi bi-people me-2 text-primary"></i>Distribution &amp; Supplier Network Impact</h6>
            <small className="text-muted">Outcomes recorded against partners transacting across KRO Exchange networks.</small>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr style={{ fontSize: '0.76rem' }}>
                    <th className="ps-3">Partner</th>
                    <th>Network</th>
                    <th>Focus</th>
                    <th className="text-center">ESG</th>
                    <th className="pe-3">Outcome underway</th>
                  </tr>
                </thead>
                <tbody>
                  {DISTRIBUTION_PARTNERS.map(p => (
                    <tr key={p.name} style={{ fontSize: '0.8rem' }}>
                      <td className="ps-3 fw-semibold">{p.name}</td>
                      <td><span className="badge bg-light text-dark border" style={{ fontSize: '0.66rem' }}>{p.network}</span></td>
                      <td className="text-muted">{p.focus}</td>
                      <td className="text-center">
                        <span className={`badge bg-${scoreTone(p.esg)}`} style={{ fontSize: '0.68rem' }}>{p.esg}</span>
                      </td>
                      <td className="text-muted pe-3" style={{ fontSize: '0.77rem' }}>{p.outcome}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            Partner-level detail lives in <Link to="/ethics/distribution-partners">Ethics → Distribution Partners</Link>.
            Products these partners distribute are listed on the{' '}
            <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer">KRO Marketplace</a>.
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: INTELLIGENCE MODULES ══════════════════ */}
      {activeTab === 'modules' && (
        <>
          <div className="alert alert-light border py-2 mb-4" style={{ fontSize: '0.8rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            This page rolls up the headline picture. Each dataset keeps a single home — follow a module below for the full detail rather than a duplicate view.
          </div>
          <div className="row g-3">
            {INTELLIGENCE_MODULES.map(m => (
              <div key={m.to} className="col-12 col-md-6 col-xl-4">
                <Link to={m.to} className="text-decoration-none">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex gap-3">
                      <div className={`p-2 rounded-3 bg-${m.tone} bg-opacity-10 flex-shrink-0`} style={{ height: 'fit-content' }}>
                        <i className={`bi ${m.icon} text-${m.tone}`} style={{ fontSize: '1.2rem' }}></i>
                      </div>
                      <div>
                        <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                          {m.title}<i className="bi bi-arrow-right ms-2" style={{ fontSize: '0.75rem' }}></i>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{m.desc}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ESGIntelligence;
